/**
 * Station repair and rearm (plan §6 M2-05, spec §7.3): hull at `career.json repairPerHull` per point and
 * torpedoes at `torpedoPrice` each, up to the ship def's full hull and rounds. Short funds refuse with the exact
 * cost (no partial service). Pure and immutable.
 *
 * **Repairs paid in plating (M8 plan §4.5 SNK-3b, D-TBD T28).** While the strategic economy prices the docked
 * station, a repair costs the hull plating it consumes: `f × (points ÷ maxHull) × platingBill(flown hull)` t, where
 * the bill is the strategic hull's `buildCost.hull_plating` for the hull whose `shipDef` is the flown ship's def
 * (`hulls.json`; the Kestrel flies as `light_fighter`, 22 t) and `f` is `sinks.json repairs.f`, the AI's rule
 * (decision 10). There is **no `freeAbove` for the player** (review 2 m10). Plating in the hold is used first, free;
 * the station's stock covers the rest at its draw price (`playerDraw`, SNK-3a). `planRepair` returns that as a
 * `ServiceDraw`; the runtime quotes and commits the draw and books it with `bookServiceDraw` after the worker
 * settled. Without a strategic link, or for a def with no plating bill, the credit price above is the fallback.
 *
 * **Torpedoes from weapon parts (M8 plan §4.5 SNK-5, D-TBD T28).** Linked, a rearm costs `sinks.json
 * munitions.torpedoT` t of weapon parts per torpedo (0.5 t: 825 cr at the 1,650 base price), the same station stock
 * the AI's magazines draw (`playerDraw` with the `playerTorpedo` sink). Weapon parts in the hold are used first,
 * free, in whole torpedoes; the station covers the rest; a short station means fewer torpedoes (whole ones only).
 * Unlinked, `career.json torpedoPrice` (800 cr) stays the fallback.
 */
import { z } from 'zod'
import hullsJson from '../data/strategic/hulls.json'
import sinksJson from '../data/strategic/sinks.json'
import type { M2Data } from '../data/m2Schemas'
import type { ShipDef } from '../data/schemas'
import type { PlayerDrawSink } from '../strategic/economy/market'
import { munitionsSinkSchema, repairsSinkSchema } from '../strategic/sinks/schema'
import { cargoClassOf, holdMultOf } from './hullFit'
import { cargoFreeT, EPSILON, GOOD_NAMES, withGood, withWallet } from './ops'
import { goodT, type StrategicGoodId } from './parts'
import { fail, formatCredits, formatTonnes, insufficientFunds, invalidAmount, ok, type CareerEffect, type CmdResult } from './result'
import type { Career } from './types'

/** Full hull and torpedo rounds of the player's ship (no module changes either). */
export type ShipLimits = Pick<ShipDef, 'hull' | 'torpedoRounds'>

type ServiceData = Pick<M2Data, 'career'>

/** The limits of the career's ship: `def` when given, else the start ship's (the only player ship in M2). */
function limitsOf(career: Career, data: ServiceData, def: ShipLimits | undefined): ShipLimits {
  if (def !== undefined) return def
  const start = data.career.start
  if (career.ship.defId !== start.ship) {
    throw new Error(`career services: ship "${career.ship.defId}" is not the start ship; pass its def limits`)
  }
  return { hull: start.hull, torpedoRounds: start.torpedoes }
}

/** Cost in whole credits (rounded up) to repair `points` hull. */
export function repairCost(data: ServiceData, points: number): number {
  return Math.ceil(points * data.career.repairPerHull - EPSILON)
}

/** Repairs `points` hull (clamped to the damage) or `'all'` of it. */
export function repair(career: Career, data: ServiceData, points: number | 'all', def?: ShipLimits): CmdResult<Career> {
  if (points !== 'all' && (!Number.isFinite(points) || points <= 0)) return invalidAmount('repair amount', points)
  const max = limitsOf(career, data, def).hull
  const missing = max - career.ship.hull
  if (missing <= EPSILON) return fail('nothingToRepair', 'The hull needs no repair.')
  const amount = points === 'all' || points > missing ? missing : points
  const cost = repairCost(data, amount)
  if (career.wallet + EPSILON < cost) return insufficientFunds(cost, career.wallet)
  const next = withWallet(career, -cost)
  const hull = amount === missing ? max : career.ship.hull + amount
  return ok({ ...next, ship: { ...next.ship, hull } })
}

/** Restocks `count` torpedoes (clamped to the empty racks) or `'all'` of them. */
export function rearm(career: Career, data: ServiceData, count: number | 'all', def?: ShipLimits): CmdResult<Career> {
  if (count !== 'all' && (!Number.isInteger(count) || count <= 0)) return invalidAmount('torpedo count', count)
  const max = limitsOf(career, data, def).torpedoRounds
  const missing = max - career.ship.torpedoes
  if (missing <= 0) return fail('torpedoesFull', 'The torpedo racks are full.')
  const n = count === 'all' || count > missing ? missing : count
  const cost = n * data.career.torpedoPrice
  if (career.wallet + EPSILON < cost) return insufficientFunds(cost, career.wallet)
  const next = withWallet(career, -cost)
  return ok({ ...next, ship: { ...next.ship, torpedoes: career.ship.torpedoes + n } })
}

/** Player-facing quote for the repair screen, e.g. "Repair 120 hull: 3,600 cr". */
export function repairQuote(career: Career, data: ServiceData, def?: ShipLimits): { points: number; cost: number; label: string } {
  const missing = Math.max(0, limitsOf(career, data, def).hull - career.ship.hull)
  const cost = missing > EPSILON ? repairCost(data, missing) : 0
  return { points: missing, cost, label: `Repair ${Math.ceil(missing - EPSILON)} hull: ${formatCredits(cost)} cr` }
}

// ---- Repairs paid in plating (M8 SNK-3b) ------------------------------------------------------------------

/** The plating-repair texts (`{pct}`: the share of the asked repair done). */
export const SERVICE_TEXTS = {
  noPlating: 'No hull plating stocked here.',
  shortPlating: 'Station short of plating: repairs {pct}%.',
  noWeaponParts: 'No weapon parts stocked here.',
  shortWeaponParts: 'Station short of weapon parts: torpedoes {pct}%.',
} as const

/** The AI's repair factor `f` (`sinks.json repairs.f`): the player pays by the same rule (decision 10). */
export const REPAIR_F: number = repairsSinkSchema.parse(sinksJson.repairs).f

const hullBillsSchema = z.object({
  hulls: z.array(z.object({ id: z.string(), shipDef: z.string(), buildCost: z.record(z.string(), z.number().nonnegative()) })),
})

/** Ship def id → the plating bill (t) of the first strategic hull that flies as it (`hulls.json`). */
const PLATING_BILL: ReadonlyMap<string, number> = (() => {
  const out = new Map<string, number>()
  for (const h of hullBillsSchema.parse(hullsJson).hulls) {
    const t = h.buildCost.hull_plating ?? 0
    if (t > 0 && !out.has(h.shipDef)) out.set(h.shipDef, t)
  }
  return out
})()

/** The hull plating bill (t) of the strategic hull whose `shipDef` is `defId`, or null (no strategic hull flies it). */
export function platingBillOf(defId: string): number | null {
  return PLATING_BILL.get(defId) ?? null
}

/** Tonnes of plating to repair `points` of a `maxHull` hull whose bill is `billT`: `f × (points ÷ maxHull) × billT`. */
export function repairPlatingT(points: number, maxHull: number, billT: number, f: number = REPAIR_F): number {
  return maxHull > 0 && points > 0 && billT > 0 ? f * (points / maxHull) * billT : 0
}

/**
 * A service paid in a good (M8 SNK-3b; SNK-5 adds the torpedo one): `needT` t of `good`, `fromHoldT` of it from the
 * hold (free) and `fromStationT` wanted from the station (`playerDraw` with `sink`). The hold is only consumed when
 * the draw is booked (`bookServiceDraw`, after the worker settled).
 */
export interface ServiceDraw {
  readonly good: StrategicGoodId
  readonly sink: PlayerDrawSink
  readonly needT: number
  readonly fromHoldT: number
  readonly fromStationT: number
  /** The refusal and notice lines: no stock of the good here, and the station short (`{pct}`). */
  readonly texts: { readonly none: string; readonly short: string }
  /**
   * Set when the service comes in whole units of `unitT` t (torpedoes, SNK-5): a short fill counts whole units
   * only, and the drawn tonnes past the last whole unit go into the hold (`bookServiceDraw`).
   */
  readonly unitT?: number
  /** The service for `share` (0..1] of `needT` applied to `career` (1: all of it). */
  apply(career: Career, share: number): Career
}

/** A planned service: booked at once (credits, or free), or a draw the runtime commits through the worker first. */
export type ServicePlan =
  | { readonly kind: 'done'; readonly career: Career; readonly effects: CareerEffect[] }
  | { readonly kind: 'draw'; readonly draw: ServiceDraw }

function planned(r: CmdResult<Career>): CmdResult<ServicePlan> {
  return r.ok ? ok<ServicePlan>({ kind: 'done', career: r.value, effects: r.effects }) : r
}

/** Where a service runs: the flown ship's limits, and `linked`: the strategic economy prices the docked station. */
export interface ServiceAt {
  readonly def?: ShipLimits
  readonly linked: boolean
}

/**
 * Plans a repair of `points` hull (or `'all'`) at `at`. Linked,
 * with a plating bill for the flown def, the repair is a hull-plating `ServiceDraw` (hold first); otherwise it is
 * the credit repair, booked at once.
 */
export function planRepair(career: Career, data: ServiceData, points: number | 'all', at: ServiceAt): CmdResult<ServicePlan> {
  const def = at.def
  const bill = at.linked ? platingBillOf(career.ship.defId) : null
  if (bill === null) return planned(repair(career, data, points, def))
  if (points !== 'all' && (!Number.isFinite(points) || points <= 0)) return invalidAmount('repair amount', points)
  const max = limitsOf(career, data, def).hull
  const missing = max - career.ship.hull
  if (missing <= EPSILON) return fail('nothingToRepair', 'The hull needs no repair.')
  const amount = points === 'all' || points > missing ? missing : points
  const needT = repairPlatingT(amount, max, bill)
  const full = amount === missing
  const apply = (c: Career, share: number): Career => {
    const hull = share >= 1 ? (full ? max : c.ship.hull + amount) : Math.min(max, c.ship.hull + amount * share)
    return { ...c, ship: { ...c.ship, hull } }
  }
  if (needT <= EPSILON) return planned(ok(apply(career, 1)))
  const held = goodT(career.cargo.parts, 'hull_plating')
  const fromHoldT = held < needT ? held : needT
  const rest = needT - fromHoldT
  const draw: ServiceDraw = {
    good: 'hull_plating',
    sink: 'playerRepair',
    needT,
    fromHoldT,
    fromStationT: rest > EPSILON ? rest : 0,
    texts: { none: SERVICE_TEXTS.noPlating, short: SERVICE_TEXTS.shortPlating },
    apply,
  }
  return ok<ServicePlan>({ kind: 'draw', draw })
}

/** Weapon parts (t) per player torpedo (`sinks.json munitions.torpedoT`, SNK-5). */
export const TORPEDO_T: number = munitionsSinkSchema.parse(sinksJson.munitions).torpedoT

/** Weapon parts (t) of the hold that a rearm of `n` torpedoes uses: whole torpedoes only, at most `n` of them. */
export function holdTorpedoT(heldT: number, n: number, perT: number = TORPEDO_T): number {
  const whole = Math.floor(heldT / perT + EPSILON)
  return (whole < n ? whole : n) * perT
}

/**
 * Plans a rearm of `count` torpedoes (or `'all'`) at `at`. Linked, it is a weapon-parts `ServiceDraw` of
 * `torpedoT` t per torpedo (hold first, in whole torpedoes; a short station gives whole torpedoes only); unlinked,
 * the credit rearm at `torpedoPrice`, booked at once.
 */
export function planRearm(career: Career, data: ServiceData, count: number | 'all', at: ServiceAt): CmdResult<ServicePlan> {
  if (!at.linked) return planned(rearm(career, data, count, at.def))
  if (count !== 'all' && (!Number.isInteger(count) || count <= 0)) return invalidAmount('torpedo count', count)
  const max = limitsOf(career, data, at.def).torpedoRounds
  const missing = max - career.ship.torpedoes
  if (missing <= 0) return fail('torpedoesFull', 'The torpedo racks are full.')
  const n = count === 'all' || count > missing ? missing : count
  const needT = n * TORPEDO_T
  const fromHoldT = holdTorpedoT(goodT(career.cargo.parts, 'weapon_parts'), n)
  const rest = needT - fromHoldT
  const apply = (c: Career, share: number): Career => {
    const got = share >= 1 ? n : Math.floor(n * share + EPSILON)
    const torpedoes = Math.min(max, c.ship.torpedoes + got)
    return { ...c, ship: { ...c.ship, torpedoes } }
  }
  const draw: ServiceDraw = {
    good: 'weapon_parts',
    sink: 'playerTorpedo',
    needT,
    fromHoldT,
    fromStationT: rest > EPSILON ? rest : 0,
    texts: { none: SERVICE_TEXTS.noWeaponParts, short: SERVICE_TEXTS.shortWeaponParts },
    unitT: TORPEDO_T,
    apply,
  }
  return ok<ServicePlan>({ kind: 'draw', draw })
}

/**
 * The share (0..1] of `draw` that `gotT` t from the station plus the planned hold tonnes cover; for a unit draw
 * (`unitT`), the share of whole units (a short torpedo fill reports the torpedo count share).
 */
export function drawShare(draw: ServiceDraw, gotT: number): number {
  const got = gotT > 0 ? gotT : 0
  if (got >= draw.fromStationT - EPSILON) return 1
  const covered = draw.fromHoldT + got
  const share = (draw.unitT === undefined ? covered : Math.floor(covered / draw.unitT + EPSILON) * draw.unitT) / draw.needT
  return share < 1 ? share : 1
}

/**
 * Books a settled draw: the planned hold tonnes leave the hold, `credits` leave the wallet, and the service is
 * applied for the share covered (`drawShare`). Refuses (nothing changed) when nothing at all is covered, the hold
 * no longer holds the planned tonnes or the wallet can't pay `credits`.
 *
 * A unit draw (`unitT`, torpedoes) filled short applies whole units only; the paid tonnes past the last whole unit
 * (always under one unit: the hold part is whole units) go into the hold as the good, even when that makes no unit
 * at all. When they don't fit in the hold's free tonnes, they make one more unit instead (the racks have room: a
 * short fill is under the asked count), so paid tonnes are never lost.
 */
export function bookServiceDraw(career: Career, draw: ServiceDraw, gotT: number, credits: number): CmdResult<Career> {
  if (!(Number.isFinite(gotT) && gotT >= 0)) return invalidAmount('drawn tonnes', gotT)
  if (!(Number.isFinite(credits) && credits >= 0)) return invalidAmount('price', credits)
  if (draw.fromHoldT + gotT <= EPSILON) return fail('notSoldHere', draw.texts.none)
  const held = goodT(career.cargo.parts, draw.good)
  if (held + EPSILON < draw.fromHoldT) {
    return fail('notInCargo', `Only ${formatTonnes(held)} t of ${GOOD_NAMES[draw.good]} in the hold.`, { held, requested: draw.fromHoldT })
  }
  if (career.wallet + EPSILON < credits) return insufficientFunds(credits, career.wallet)
  let next = draw.fromHoldT > 0 ? withGood(career, draw.good, -draw.fromHoldT) : career
  if (credits > 0) next = withWallet(next, -credits)
  let share = drawShare(draw, gotT)
  const unit = draw.unitT
  if (unit !== undefined && share < 1) {
    const left = draw.fromHoldT + gotT - share * draw.needT
    if (left > EPSILON) {
      // M10 P5a6b H3: the leftover takes its class-weighted hold (÷ 1 on every shipped hull).
      if (left / holdMultOf(next.ship.defId, cargoClassOf(draw.good)) <= cargoFreeT(next) + EPSILON) next = withGood(next, draw.good, left)
      else share = Math.min(1, share + unit / draw.needT)
    }
  }
  return ok(draw.apply(next, share))
}

/** The short-stock line for a draw that covered `share` of the service. */
export function shortLine(draw: ServiceDraw, share: number): string {
  return draw.texts.short.replace('{pct}', String(Math.floor(share * 100 + EPSILON)))
}
